import {
  ExportOffer,
  ImportOffer,
  GeneralOffer,
  FreeCarrierOffer,
  PostImportOffer,
  FilterTab,
  FilterTabData
} from 'src/features/stocks/ducks/records'
import {
  Company,
  Container,
  Contractor,
  Currency,
  Location,
  Shipowner,
  Document
} from 'src/ducks/records'
import {
  head,
  isNil,
  evolve,
  pipe,
  assoc,
  pathOr,
  flatten,
  keys,
  reverse,
  findIndex,
  propEq
} from 'ramda'
import moment from 'moment'
import {
  isEmptyArray,
  isEmptyString,
  isArray,
  isFalsy,
  renameKeysWith
} from 'ramda-adjunct'
import camelCase from 'lodash.camelcase'
import {
  removeMsFromTime,
  filterFalsyValues,
  addPostalCodeToName
} from 'src/utils/helpers'
import translate from 'src/intl/translate'
import {
  ADR_OPTIONS,
  GENSET_OPTIONS,
  RESERVATION_STATUS_OPTIONS,
  VISIBILITY_OPTIONS
} from './consts'
import normalizeTabName from './normalizeTabName'

export const normalizeExportOfferDetails = offer =>
  new ExportOffer({
    ...offer,
    loadingDate: moment(offer.loadingDate).calendar(),
    loadingTime: removeMsFromTime(offer.loadingTime),
    returnDate: moment(offer.returnDate).calendar(),
    returnTime: removeMsFromTime(offer.returnTime),
    price: Currency({ ...offer.price }),
    container: Container({ ...offer.container }),
    issuer: Contractor({
      ...offer.issuer,
      company: Company({
        ...offer.issuer?.company,
        documents: pathOr(
          [],
          ['issuer', 'company', 'documents'],
          offer
        ).map(file => Document({ ...file }))
      })
    }),
    handler: Contractor({ ...offer.handler }),
    shipowner: Shipowner({ ...offer.shipowner }),
    loadingLocation: Location(addPostalCodeToName(offer.loadingLocation)),
    unloadingLocation: Location(addPostalCodeToName(offer.unloadingLocation)),
    customsClearanceLocation: Location(
      addPostalCodeToName(offer.customsClearanceLocation)
    ),
    returnLocation: Location(addPostalCodeToName(offer.returnLocation)),
    timeFromAdded: moment(offer.addedToListingAt).fromNow(),
    isOfferNew:
      moment(new Date().getTime()).diff(offer.addedToListingAt, 'minutes') < 1,

    isPrivate: offer.visibility === 'private',
    goPublicAt: isFalsy(offer.goPublicAt) ? null : moment(offer.goPublicAt)
  })

export const normalizeImportOfferDetails = offer =>
  new ImportOffer({
    ...offer,
    pickupDate: moment(offer.pickupDate).calendar(),
    pickupTime: removeMsFromTime(offer.pickupTime),
    unloadingDate: moment(offer.unloadingDate).calendar(),
    unloadingTime: removeMsFromTime(offer.unloadingTime),
    price: Currency({ ...offer.price }),
    container: Container({ ...offer.container }),
    issuer: Contractor({ ...offer.issuer }),
    handler: Contractor({ ...offer.handler }),
    shipowner: Shipowner({ ...offer.shipowner }),
    pickupLocation: Location(addPostalCodeToName(offer.pickupLocation)),
    unloadingLocation: Location(addPostalCodeToName(offer.unloadingLocation)),
    returnLocation: Location(addPostalCodeToName(offer.returnLocation)),
    customsClearanceLocation: Location(
      addPostalCodeToName(offer.customsClearanceLocation)
    ),
    timeFromAdded: moment(offer.addedToListingAt).fromNow(),
    isOfferNew:
      moment(new Date().getTime()).diff(offer.addedToListingAt, 'minutes') < 1,

    isPrivate: offer.visibility === 'private',
    goPublicAt: isFalsy(offer.goPublicAt) ? null : moment(offer.goPublicAt)
  })

export const normalizeGeneralOfferDetails = offer =>
  new GeneralOffer({
    ...offer,
    loadingDate: moment(offer.loadingDate).calendar(),
    loadingTime: removeMsFromTime(offer.loadingTime),
    unloadingDate: moment(offer.unloadingDate).calendar(),
    unloadingTime: removeMsFromTime(offer.unloadingTime),
    price: Currency({ ...offer.price }),
    container: Container({ ...offer.container }),
    issuer: Contractor({ ...offer.issuer }),
    handler: Contractor({ ...offer.handler }),
    loadingLocation: Location(addPostalCodeToName(offer.loadingLocation)),
    unloadingLocation: Location(addPostalCodeToName(offer.unloadingLocation)),
    customsClearanceLocation: Location(
      addPostalCodeToName(offer.customsClearanceLocation)
    ),
    timeFromAdded: moment(offer.addedToListingAt).fromNow(),
    isOfferNew:
      moment(new Date().getTime()).diff(offer.addedToListingAt, 'minutes') < 1,

    isPrivate: offer.visibility === 'private',
    goPublicAt: isFalsy(offer.goPublicAt) ? null : moment(offer.goPublicAt)
  })

export const normalizePostImportOfferDetails = offer =>
  new PostImportOffer({
    ...offer,
    availabilityDate: moment(offer.availabilityDate).calendar(),
    availabilityTime: removeMsFromTime(offer.availabilityTime),
    container: Container({ ...offer.container }),
    price: Currency({ ...offer.price }),
    issuer: Contractor({ ...offer.issuer }),
    handler: Contractor({ ...offer.handler }),
    shipowner: Shipowner({ ...offer.shipowner }),
    availabilityLocation: Location(
      addPostalCodeToName(offer.availabilityLocation)
    ),
    customsClearanceLocation: Location(
      addPostalCodeToName(offer.customsClearanceLocation)
    ),
    destinationLocation: Location(
      addPostalCodeToName(offer.destinationLocation)
    ),
    timeFromAdded: moment(offer.addedToListingAt).fromNow(),
    isOfferNew:
      moment(new Date().getTime()).diff(offer.addedToListingAt, 'minutes') < 1,

    isPrivate: offer.visibility === 'private',
    goPublicAt: isFalsy(offer.goPublicAt) ? null : moment(offer.goPublicAt)
  })

export const normalizeFreeCarrierOfferDetails = offer =>
  new FreeCarrierOffer({
    ...offer,
    availabilityDate: moment(offer.availabilityDate).calendar(),
    availabilityTime: removeMsFromTime(offer.availabilityTime),
    containerSemiTrailer: Container({ ...offer.containerSemiTrailer }),
    price: Currency({ ...offer.price }),
    issuer: Contractor({ ...offer.issuer }),
    handler: Contractor({ ...offer.handler }),
    availabilityLocation: Location(
      addPostalCodeToName(offer.availabilityLocation)
    ),
    destinationLocation: Location(
      addPostalCodeToName(offer.destinationLocation)
    ),
    customsClearanceLocation: Location(
      addPostalCodeToName(offer.customsClearanceLocation)
    ),
    timeFromAdded: moment(offer.addedToListingAt).fromNow(),
    isOfferNew:
      moment(new Date().getTime()).diff(offer.addedToListingAt, 'minutes') < 1,

    isPrivate: offer.visibility === 'private',
    goPublicAt: isFalsy(offer.goPublicAt) ? null : moment(offer.goPublicAt)
  })

export const normalizeExportOffers = arr =>
  arr.map(offer => normalizeExportOfferDetails(offer))

export const normalizeImportOffers = arr =>
  arr.map(offer => normalizeImportOfferDetails(offer))

export const normalizeGeneralOffers = arr =>
  arr.map(offer => normalizeGeneralOfferDetails(offer))

export const normalizePostImportOffers = arr =>
  arr.map(offer => normalizePostImportOfferDetails(offer))

export const normalizeFreeCarriersOffers = arr =>
  arr.map(offer => normalizeFreeCarrierOfferDetails(offer))

const normalizeLocationName = (id, locations = []) => {
  //ids are received from API as `id|range`
  const option = head(locations.filter(v => v.id === head(id.split('|'))))

  return isNil(option)
    ? head(id.split('|'))
    : filterFalsyValues([
        option.countryCode,
        option.name,
        option.postalCode
      ]).join(', ')
}

//ids are received from API as `id|range`
const normalizeToLocation = (ids, locations) =>
  (isArray(ids) ? ids : [ids])
    .map(id => ({
      id: id.split('|')[0],
      name: normalizeLocationName(id, locations),
      range: isFalsy(id.split('|')[1]) ? 0 : parseInt(id.split('|')[1], 10)
    }))

const normalizeToValueArray = value => (isArray(value) ? value : [value])

const normalizeCompanyName = (id, companies = []) => {
  const option = head(companies.filter(v => v.id === id))
  return `${option.name}`
}

const normalizeToCompany = (ids, companies) =>
  isArray(ids)
    ? ids.map(id => ({ id, name: normalizeCompanyName(id, companies) }))
    : [{ id: ids, name: normalizeCompanyName(ids, companies) }]

export const normalizeFilterTabs = (tabs, locations, companies) =>
  tabs.map(tab => {
    const tabName = normalizeTabName(tab.name)
    const currentFilters = pipe(
      evolve({
        // Existing location normalizers
        loadingLocation: ids => normalizeToLocation(ids, locations),
        returnLocation: ids => normalizeToLocation(ids, locations),
        pickupLocation: ids => normalizeToLocation(ids, locations),
        unloadingLocation: ids => normalizeToLocation(ids, locations),
        availabilityLocation: ids => normalizeToLocation(ids, locations),
        destinationLocation: ids => normalizeToLocation(ids, locations),

        // Add country normalizers for each location
        loadingLocationCountry: normalizeToValueArray,
        returnLocationCountry: normalizeToValueArray,
        pickupLocationCountry: normalizeToValueArray,
        unloadingLocationCountry: normalizeToValueArray,
        availabilityLocationCountry: normalizeToValueArray,
        destinationLocationCountry: normalizeToValueArray,

        // Rest of existing normalizers
        container: normalizeToValueArray,
        shipowner: normalizeToValueArray,
        company: ids => normalizeToCompany(ids, companies),
        europeanEmissionStandard: value => parseInt(value, 10),
        containerSemiTrailer: normalizeToValueArray,
        adrClass: normalizeToValueArray,
        cargoType: normalizeToValueArray
      }),
      assoc('tabName', tabName)
    )(tab.currentFilters)

    return new FilterTab({
      ...tab,
      name: tabName,
      currentFilters: isNil(tab.currentFilters) ? null : currentFilters
    })
  })

const getFirstLocationName = arr => (isEmptyArray(arr) ? null : head(arr).name)

const getFirstCompanyName = arr => (isEmptyArray(arr) ? null : head(arr).name)

const formatDateForTabName = date =>
  isNil(date) ? null : moment(date).format('DD.MM')

const formatEuropeanEmissionStandard = value =>
  isFalsy(value) ? null : `EURO ${value}`

// Add this helper function to get the first country from an array
const getCountriesLabel = arr => {
  if (isEmptyArray(arr)) {
    return null
  }

  const countriesToShow = arr.slice(0, 3)
  const countriesString = countriesToShow.join(', ')

  return arr.length > 3 ? `${countriesString}...` : countriesString
}

// const getFirstCountryName = arr => (isEmptyArray(arr) ? null : head(arr))

const getDictionaryLabel = (id, dictionary = []) => {
  if (isNil(id)) return null
  const option = head(dictionary.filter(v => v.value === id))

  return !isFalsy(option)
    ? translate().formatMessage({ id: option.label })
    : null
}

const getReservationStatusDictionaryLabel = (id, dictionary = []) => {
  if (isNil(id)) return null
  const option = head(dictionary.filter(v => v.value === id))
  return !isFalsy(option)
    ? translate().formatMessage({ id: option.label })
    : null
}

const getVisibilityDictionaryLabel = (id, dictionary = []) => {
  if (isNil(id)) return null
  const option = head(dictionary.filter(v => v.value === id))
  return !isFalsy(option)
    ? translate().formatMessage({ id: option.label })
    : null
}

const getBooleanFilterLabel = (labelId, value) => {
  if (isNil(value) || value === '') return null

  const normalizedValue = value === true || value === '1' || value === 1

  return `${translate().formatMessage({
    id: labelId
  })}: ${translate().formatMessage({
    id: normalizedValue ? 'common.yes' : 'common.no'
  })}`
}

export const generateTabNameExport = (filters, dictionaries) => {
  const filtersRow = [
    getFirstLocationName(filters.loadingLocation),
    getCountriesLabel(filters.loadingLocationCountry), // Add loading location country
    formatDateForTabName(filters.loadingDateFrom),
    formatDateForTabName(filters.loadingDateTo),
    getFirstLocationName(filters.returnLocation),
    getCountriesLabel(filters.returnLocationCountry), // Add return location country
    formatDateForTabName(filters.returnDateFrom),
    formatDateForTabName(filters.returnDateTo),
    getDictionaryLabel(head(filters.container), dictionaries.containers),
    getDictionaryLabel(head(filters.shipowner), dictionaries.shipowners),
    getFirstCompanyName(filters.company),
    getDictionaryLabel(filters.adr, ADR_OPTIONS),
    getReservationStatusDictionaryLabel(
      filters.status,
      RESERVATION_STATUS_OPTIONS
    ),
    getVisibilityDictionaryLabel(filters.visibility, VISIBILITY_OPTIONS),
    getDictionaryLabel(head(filters.adrClass), dictionaries.adrClasses),
    formatEuropeanEmissionStandard(filters.europeanEmissionStandard),
    getDictionaryLabel(head(filters.cargoType), dictionaries.cargoTypes)
  ]

  const filterName = filterFalsyValues(filtersRow).join(', ')
  return isEmptyString(filterName) ? null : filterName
}

export const generateTabNameImport = (filters, dictionaries) => {
  const filtersRow = [
    getFirstLocationName(filters.pickupLocation),
    formatDateForTabName(filters.pickupDateFrom),
    formatDateForTabName(filters.pickupDateTo),
    getFirstLocationName(filters.unloadingLocation),
    formatDateForTabName(filters.unloadingDateFrom),
    formatDateForTabName(filters.unloadingDateTo),
    getDictionaryLabel(head(filters.container), dictionaries.containers),
    getDictionaryLabel(head(filters.shipowner), dictionaries.shipowners),
    getFirstCompanyName(filters.company),
    getDictionaryLabel(filters.adr, ADR_OPTIONS),
    getDictionaryLabel(filters.status, RESERVATION_STATUS_OPTIONS),
    getVisibilityDictionaryLabel(filters.visibility, VISIBILITY_OPTIONS),
    getDictionaryLabel(head(filters.adrClass), dictionaries.adrClasses),
    formatEuropeanEmissionStandard(filters.europeanEmissionStandard),
    getDictionaryLabel(head(filters.cargoType), dictionaries.cargoTypes),
    getCountriesLabel(filters.pickupLocationCountry), // Add pickup location country
    getCountriesLabel(filters.unloadingLocationCountry) // Add unloading location country
  ]

  const filterName = filterFalsyValues(filtersRow).join(', ')
  return isEmptyString(filterName) ? null : filterName
}

export const generateTabNameGeneral = (filters, dictionaries) => {
  const filtersRow = [
    getFirstLocationName(filters.loadingLocation),
    formatDateForTabName(filters.loadingDateFrom),
    formatDateForTabName(filters.loadingDateTo),
    getFirstLocationName(filters.unloadingLocation),
    formatDateForTabName(filters.unloadingDateFrom),
    formatDateForTabName(filters.unloadingDateTo),
    getDictionaryLabel(head(filters.container), dictionaries.containers),
    getFirstCompanyName(filters.company),
    getDictionaryLabel(filters.adr, ADR_OPTIONS),
    getDictionaryLabel(filters.status, RESERVATION_STATUS_OPTIONS),
    getVisibilityDictionaryLabel(filters.visibility, VISIBILITY_OPTIONS),
    getDictionaryLabel(head(filters.adrClass), dictionaries.adrClasses),
    formatEuropeanEmissionStandard(filters.europeanEmissionStandard),
    getDictionaryLabel(head(filters.cargoType), dictionaries.cargoTypes),
    getCountriesLabel(filters.loadingLocationCountry),
    getCountriesLabel(filters.unloadingLocationCountry),
    getBooleanFilterLabel('offersFilters.sent', filters.isSentRequired),
    getBooleanFilterLabel('offersFilters.line', filters.isLine)
  ]

  const filterName = filterFalsyValues(filtersRow).join(', ')
  return isEmptyString(filterName) ? null : filterName
}

export const generateTabNameFreeCarriers = (filters, dictionaries) => {
  const filtersRow = [
    getFirstLocationName(filters.availabilityLocation),
    formatDateForTabName(filters.availabilityDateFrom),
    formatDateForTabName(filters.availabilityDateTo),
    getFirstLocationName(filters.destinationLocation),
    getDictionaryLabel(
      head(filters.containerSemiTrailer),
      dictionaries.containerSemiTrailers
    ),
    getFirstCompanyName(filters.company),
    getDictionaryLabel(filters.adr, ADR_OPTIONS),
    getDictionaryLabel(filters.genset, GENSET_OPTIONS),
    getDictionaryLabel(filters.status, RESERVATION_STATUS_OPTIONS),
    getVisibilityDictionaryLabel(filters.visibility, VISIBILITY_OPTIONS),
    getDictionaryLabel(head(filters.adrClass), dictionaries.adrClasses),
    formatEuropeanEmissionStandard(filters.europeanEmissionStandard),
    getCountriesLabel(filters.availabilityLocationCountry),
    getCountriesLabel(filters.destinationLocationCountry)
  ]

  const filterName = filterFalsyValues(filtersRow).join(', ')
  return isEmptyString(filterName) ? null : filterName
}

export const generateTabNamePostImport = (filters, dictionaries) => {
  const filtersRow = [
    getFirstLocationName(filters.availabilityLocation),
    formatDateForTabName(filters.availabilityDateFrom),
    formatDateForTabName(filters.availabilityDateTo),
    getFirstLocationName(filters.destinationLocation),
    getDictionaryLabel(head(filters.container), dictionaries.containers),
    getVisibilityDictionaryLabel(filters.visibility, VISIBILITY_OPTIONS),
    getDictionaryLabel(head(filters.adrClass), dictionaries.adrClasses),
    formatEuropeanEmissionStandard(filters.europeanEmissionStandard),
    getCountriesLabel(filters.availabilityLocationCountry),
    getCountriesLabel(filters.destinationLocationCountry)
  ]

  const filterName = filterFalsyValues(filtersRow).join(', ')
  return isEmptyString(filterName) ? null : filterName
}

export const normalizeToLocationsIds = tabs => {
  const tabsLocations = tabs.map(tab => {
    const filtersLocations = [
      pathOr([], ['currentFilters', 'loadingLocation'], tab),
      pathOr([], ['currentFilters', 'returnLocation'], tab),
      pathOr([], ['currentFilters', 'pickupLocation'], tab),
      pathOr([], ['currentFilters', 'unloadingLocation'], tab),
      pathOr([], ['currentFilters', 'availabilityLocation'], tab),
      pathOr([], ['currentFilters', 'destinationLocation'], tab)
    ]
    return flatten(filtersLocations)
  })
  return flatten(tabsLocations)
}

export const normalizeToCompaniesIds = tabs => {
  const tabsCompanies = tabs.map(tab =>
    pathOr([], ['currentFilters', 'company'], tab)
  )
  return flatten(tabsCompanies)
}

const toCamelCaseDeep = obj => {
  const normalized = renameKeysWith(camelCase)(obj)
  return keys(normalized).reduce((acc, key) => {
    //parse nested objects
    acc[key] =
      typeof normalized[key] === 'object' && !(normalized[key] instanceof Array)
        ? renameKeysWith(camelCase)(normalized[key])
        : normalized[key]
    return acc
  }, {})
}
export const refreshExchangeAfterOfferBeingRefreshed = ({
  exchangeState,
  tabId,
  eventPayload,
  offerNormalizer
}) => {
  let state = exchangeState
  const tabExists = !isFalsy(exchangeState.tabsData[tabId])
  const tabsDataObject = exchangeState.tabsData
  const isActiveTab = exchangeState.activeTab === tabId
  const normalizedOffer = offerNormalizer(toCamelCaseDeep(eventPayload))

  if (isActiveTab) {
    //if current tab is active and current page is different than 1 then increment infoBox value
    if (state.pagination.currentPage !== 1) {
      return state.update('infoBoxOffersNumber', v => v + 1)
    }

    //if current tab is active and currentPage is equal to 1 then add it to the list
    const offerIndex = findIndex(propEq('id', normalizedOffer.id), state.offers)
    return state
      .update('offers', list =>
        list.map((listOffer, index) =>
          index === offerIndex
            ? listOffer
                .set('liveNewOfferAddedAt', new Date().getTime())
                .set('isOfferNew', true)
                .set('isOfferLiveNew', true)
            : listOffer
        )
      )
      .update('offers', list =>
        offerIndex === -1
          ? [
              normalizedOffer
                .set('liveNewOfferAddedAt', new Date().getTime())
                .set('isOfferNew', true)
                .set('isOfferLiveNew', true),
              ...list
            ]
          : list
      )
  }

  //if current tab is not active, store offer in tabsData
  tabsDataObject[tabId] = tabExists
    ? tabsDataObject[tabId]
        .update('counter', v => v + 1)
        .update('addedOffers', v => [
          offerNormalizer(toCamelCaseDeep(eventPayload)),
          ...v
        ])
    : new FilterTabData({
        counter: 1,
        addedOffers: [offerNormalizer(toCamelCaseDeep(eventPayload))]
      })

  return state
}

export const updateExchangeAfterNewLiveOfferCreatedEvent = ({
  exchangeState,
  tabId,
  eventPayload,
  offerNormalizer
}) => {
  let state = exchangeState
  const tabExists = !isFalsy(exchangeState.tabsData[tabId])
  const tabsDataObject = exchangeState.tabsData
  const isActiveTab = exchangeState.activeTab === tabId
  const normalizedOffer = offerNormalizer(toCamelCaseDeep(eventPayload))

  if (isActiveTab) {
    //if current tab is active and current page is different than 1 then increment infoBox value
    if (state.pagination.currentPage !== 1) {
      return state.update('infoBoxOffersNumber', v => v + 1)
    }

    //if current tab is active and currentPage is equal to 1 then add it to the list
    const offerIndex = findIndex(propEq('id', normalizedOffer.id), state.offers)
    return state
      .update('offers', list =>
        list.map((listOffer, index) =>
          index === offerIndex
            ? listOffer
                .set('liveNewOfferAddedAt', new Date().getTime())
                .set('isOfferNew', true)
                .set('isOfferLiveNew', true)
                .set('liveOfferRemovedAt', null)
            : listOffer
        )
      )
      .update('offers', list =>
        offerIndex === -1
          ? [
              normalizedOffer
                .set('liveNewOfferAddedAt', new Date().getTime())
                .set('isOfferNew', true)
                .set('isOfferLiveNew', true),
              ...list
            ]
          : list
      )
      .updateIn(['pagination', 'total'], v => v + 1)
      .setIn(
        ['offerDetails', 'deletedAt'],
        exchangeState.offerDetails.id === eventPayload.id
          ? null
          : exchangeState.offerDetails.deletedAt
      )
  }

  //if current tab is not active, store offer in tabsData
  tabsDataObject[tabId] = tabExists
    ? tabsDataObject[tabId]
        .update('counter', v => v + 1)
        .update('addedOffers', v => [
          offerNormalizer(toCamelCaseDeep(eventPayload)),
          ...v
        ])
    : new FilterTabData({
        counter: 1,
        addedOffers: [offerNormalizer(toCamelCaseDeep(eventPayload))]
      })

  return state
}

export const updateExchangeAfterSwitchToNewTabWithLiveData = ({
  exchangeState,
  tabData,
  tabId
}) => {
  let state = exchangeState
  //reverse live offers list to keep the order
  reverse(tabData.addedOffers).map(liveOffer => {
    const offerIndex = findIndex(propEq('id', liveOffer.id), state.offers)
    state = state
      .update('offers', list =>
        list.map((listOffer, index) =>
          index === offerIndex
            ? listOffer
                .set('liveNewOfferAddedAt', new Date().getTime())
                .set('isOfferNew', true)
                .set('isOfferLiveNew', true)
            : listOffer
        )
      )
      .update('offers', list =>
        offerIndex === -1
          ? [
              liveOffer
                .set('liveNewOfferAddedAt', new Date().getTime())
                .set('isOfferNew', true)
                .set('isOfferLiveNew', true),
              ...list
            ]
          : list
      )
  })

  state.tabsData[tabId] = state.tabsData[tabId]
    .set('addedOffers', [])
    .set('counter', 0)

  return state
}

//isOfferLiveNew - after 2 minutes mark offer as not new
//isOfferNew - after 1 minute mark offer as not new

export const updateOfferTimestamps = offers =>
  offers.map(offer =>
    offer
      .set('timeFromAdded', moment(offer.addedToListingAt).fromNow())
      .set(
        'isOfferLiveNew',
        !isNil(offer.liveNewOfferAddedAt)
          ? moment(new Date().getTime()).diff(
              offer.liveNewOfferAddedAt,
              'minutes'
            ) < 2
          : null
      )
      .set(
        'isOfferNew',
        !isNil(offer.addedToListingAt)
          ? moment(new Date().getTime()).diff(
              offer.addedToListingAt,
              'minutes'
            ) < 1
          : null
      )
  )

export const updateExchangeAfterOfferDeletedEvent = ({
  exchangeState,
  eventPayload,
  tabId
}) => {
  const isActiveTab = exchangeState.activeTab === tabId
  if (isActiveTab) {
    return exchangeState
      .update('offers', list =>
        list.map(listOffer =>
          listOffer.id === eventPayload.id
            ? listOffer.set('liveOfferRemovedAt', new Date().getTime())
            : listOffer
        )
      )
      .updateIn(['pagination', 'total'], v => (v === 0 ? 0 : v - 1))
      .setIn(
        ['offerDetails', 'deletedAt'],
        exchangeState.offerDetails.id === eventPayload.id
          ? new Date().getTime()
          : null
      )
  }

  return exchangeState
}

export const normalizeExportOfferTimelineData = offer => [
  {
    title: 'loadingLocation',
    location: offer.loadingLocation,
    date: offer.loadingDate,
    time: offer.loadingTime
  },
  {
    title: 'returnLocation',
    location: offer.returnLocation,
    date: offer.returnDate,
    time: offer.returnTime
  }
]

export const normalizeImportOfferTimelineData = offer => [
  {
    title: 'pickupLocation',
    location: offer.pickupLocation,
    date: offer.pickupDate,
    time: offer.pickupTime
  },
  {
    title: 'unloadingLocation',
    location: offer.unloadingLocation,
    date: offer.unloadingDate,
    time: offer.unloadingTime
  },
  {
    title: 'returnLocation',
    location: offer.returnLocation
  }
]

export const normalizeGeneralOfferTimelineData = offer => [
  {
    title: 'loadingLocation',
    location: offer.loadingLocation,
    date: offer.loadingDate,
    time: offer.loadingTime
  },
  {
    title: 'unloadingLocation',
    location: offer.unloadingLocation,
    date: offer.unloadingDate,
    time: offer.unloadingTime
  }
]

export const normalizeFreeCarrierOfferTimelineData = offer => [
  {
    title: 'availabilityLocation',
    location: offer.availabilityLocation,
    date: offer.availabilityDate,
    time: offer.availabilityTime
  },
  {
    title: 'destinationLocation',
    location: offer.destinationLocation
  }
]

export const normalizePostImportOfferTimelineData = offer => [
  {
    title: 'availabilityLocation',
    location: offer.availabilityLocation,
    date: offer.availabilityDate,
    time: offer.availabilityTime
  },
  {
    title: 'destinationLocation',
    location: offer.destinationLocation
  }
]

export const normalizeContractExportOfferTimelineData = offer => [
  {
    title: 'loadingLocation',
    location: offer.originLocation,
    count: offer.numberOfContainers
  },
  {
    title: 'returnLocation',
    location: offer.destinationLocation
  }
]

export const normalizeContractImportOfferTimelineData = offer => [
  {
    title: 'pickupLocation',
    location: offer.originLocation,
    count: offer.numberOfContainers
  },
  {
    title: 'unloadingLocation',
    location: offer.destinationLocation
  },
  {
    title: 'returnLocation',
    location: offer.returnLocation
  }
]
